import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {withTranslation} from 'react-i18next';

import {UserContextConsumer} from 'context/UserContext';
import {ChatContextConsumer} from 'context/ChatContext';

import PersonalBasicsForm from 'components/Welcome/Form/PersonalBasicsForm/PersonalBasicsForm';
import WorkDetailsForm from 'components/Welcome/Form/WorkDetailsForm/WorkDetailsForm';
import AddressForm from 'components/Welcome/Form/AddressForm/AddressForm';
import CompanyAddressForm from 'components/Welcome/Form/CompanyAddressForm/CompanyAddressForm';
import AboutCompanyForm from 'components/Welcome/Form/AboutCompanyForm/AboutCompanyForm';
import TranslationForm from 'components/Welcome/Form/TranslationForm/TranslationForm';
import MedicalForm from 'components/Welcome/Form/MedicalForm/MedicalForm';
import BusinessForm from 'components/Welcome/Form/BusinessForm/BusinessForm';
import SimpleMessage from 'components/common/SimpleMessage';

import {
    validatePersonalBasics,
    validateWorkDetails,
    validateTranslation,
    validateAboutCompany,
    validateCompanyAddress,
    validateMedicalData,
    validateBusinessData,
    validateLength,
    validatePaymentDetails,
    validateRepresentative,
    validateSingleField,
} from './validation';
import {stripPhoneNumber} from 'helpers/formatter';

import UserRoles, {isCompanyAdmin, isModerator, isRespondent, isTranslator} from 'constants/UserRoles';
import {isDeleted} from 'constants/UserBusinessMedical';

import style from 'components/Welcome/WelcomePage.module.scss';
import PaymentDetailsForm from 'components/Welcome/Form/PaymentDetailsForm/PaymentDetailsForm';
import OptionSlider from 'components/common/Form/OptionSlider/OptionSlider';
import AccountTypeForm from 'components/Welcome/Form/AccountTypeForm/AccountTypeForm';
import cn from 'classnames';
import validateVatNumber from 'helpers/validateVatNumber';
import CompanyRepresentativeForm from 'components/Welcome/Form/CompanyRepresentativeForm/CompanyRepresentativeForm';
import {NO_COMPANY_STATUSES} from 'constants/Experience';
import {BackIcon} from 'components/common/icons';
import {STEPS_FIELDS} from 'constants/UserProfileComplete';
import {Redirect} from 'react-router-dom';
import {getRedirectPath} from 'constants/Routes';
import getLocationAutocomplete from 'helpers/getLocationAutocomplete';
import {interviewTypesValues} from 'components/Interview/interviewConstants';
import {ACCOUNT_TYPES_COMPANY_ADMIN} from 'constants/AccountTypeInformation';
import {isAccountTypeIndividual} from 'components/common/Form/AccountTypeSelect/const';

const STEPS = {
    accountType: 'welcome:text.accountType',
    personalBasics: 'welcome:text.personalBasics',
    address: 'welcome:text.address',
    personalAddress: 'welcome:text.personalAddress',
    workDetails: 'welcome:text.workDetails',
    paymentDetails: 'welcome:text.paymentDetails',
    translation: 'welcome:text.translation',
    aboutCompany: 'welcome:text.aboutCompany',
    companyAddress: 'welcome:text.companyAddress',
    companyRepresentative: 'welcome:text.companyRepresentative',
    isBusiness: 'welcome:text.business',
    isMedical: 'welcome:text.medical',
};

const HEADER_TEXTS = {
    personalBasics: 'welcome:text.completeProfile',
    address: 'welcome:text.tradingAddress',
    personalAddress: 'welcome:text.personalAddress',
    workDetails: 'welcome:text.workDetails',
    paymentDetails: 'welcome:text.yourCosts',
    accountType: 'welcome:text.accountTypeUse',
    aboutCompany: 'welcome:text.companyProfile',
    companyAddress: 'welcome:text.companyTradingAddress',
    companyRepresentative: 'welcome:text.finalStep',
    translation: 'welcome:text.finalStep',
};

const SUBHEADER_TEXTS = {
    personalBasics: 'welcome:text.helpBrands',
    address: 'welcome:text.verifyInfo',
    personalAddress: 'welcome:text.verifyInfo',
    workDetails: 'welcome:text.helpBrands',
    paymentDetails: 'welcome:text.lastStep',
    aboutCompany: 'welcome:text.tellCompany',
    companyAddress: 'welcome:text.provideInformation',
    companyRepresentative: 'welcome:text.representativeInfo',
    translation: 'welcome:text.lastStep',

    personalAddressCompanyAdmin: 'welcome:text.verifyInfoClient',
};

const getTextByStep = (step, userRole, isHeader, accountType) => {
    const stepKey = Object.keys(STEPS).find(key => STEPS[key] === step);
    const showTextForIndividual =
        isAccountTypeIndividual(accountType || ACCOUNT_TYPES_COMPANY_ADMIN.individual) &&
        stepKey === STEPS.personalBasics;

    if (!showTextForIndividual && userRole === 'CompanyAdmin' && !isHeader) {
        return '';
    }

    return isHeader
        ? HEADER_TEXTS[stepKey + userRole] || HEADER_TEXTS[stepKey]
        : SUBHEADER_TEXTS[stepKey + userRole] || SUBHEADER_TEXTS[stepKey];
};

const COMPANY_ADMIN_FIRST_STEPS = [STEPS.accountType];
const COMPANY_STEPS = [STEPS.aboutCompany, STEPS.companyAddress, STEPS.companyRepresentative];
const PERSONAL_STEPS = [STEPS.personalBasics, STEPS.personalAddress];

const COMPANY_ADMIN_INDIVIDUAL = [...COMPANY_ADMIN_FIRST_STEPS, ...PERSONAL_STEPS];
const COMPANY_ADMIN_COMPANY = [...COMPANY_ADMIN_FIRST_STEPS, ...COMPANY_STEPS];

const RESOURCE_STEPS = [...PERSONAL_STEPS, STEPS.paymentDetails];

const MODERATOR_STEPS = [...COMPANY_ADMIN_FIRST_STEPS, STEPS.personalBasics, STEPS.address, STEPS.paymentDetails];
const MODERATOR_COMPANY_STEPS = [...COMPANY_ADMIN_FIRST_STEPS, ...COMPANY_STEPS, STEPS.paymentDetails];

const TRANSLATOR_STEPS = [
    ...COMPANY_ADMIN_FIRST_STEPS,
    STEPS.personalBasics,
    STEPS.address,
    STEPS.paymentDetails,
    STEPS.translation,
];
const TRANSLATOR_COMPANY_STEPS = [
    ...COMPANY_ADMIN_FIRST_STEPS,
    ...COMPANY_STEPS,
    STEPS.paymentDetails,
    STEPS.translation,
];

const STEPS_OBJECT = {
    company: {
        moderator: MODERATOR_COMPANY_STEPS,
        translator: TRANSLATOR_COMPANY_STEPS,
        companyadmin: COMPANY_ADMIN_COMPANY,
    },
    individual: {
        respondent: RESOURCE_STEPS,
        moderator: MODERATOR_STEPS,
        translator: TRANSLATOR_STEPS,
        companyadmin: COMPANY_ADMIN_INDIVIDUAL,
    },
};

class Welcome extends Component {
    static propTypes = {
        userContext: PropTypes.object,
    };

    constructor(props) {
        super(props);

        const {
            user: {hourlyRate, birthday, company, ownCompany, role, timezone, costPerHour, ...user},
        } = props.userContext;

        const {t} = props;

        const isMedical = !isDeleted(user.medicalStatus);
        const isBusiness = !isDeleted(user.businessStatus);
        let steps = this.getDefaultSteps(role, t);
        if (isMedical) steps.push(STEPS.isMedical);
        if (isBusiness) steps.push(STEPS.isBusiness);

        if (user.accountType === 'company') {
            if (isCompanyAdmin(role)) steps = STEPS_OBJECT.company.companyadmin;
            if (isModerator(role)) steps = STEPS_OBJECT.company.moderator;
            if (isTranslator(role)) steps = STEPS_OBJECT.company.translator;
        }
        if (user.accountType === 'individual') {
            if (isCompanyAdmin(role)) steps = STEPS_OBJECT.individual.companyadmin;
            if (isModerator(role)) steps = STEPS_OBJECT.individual.moderator;
            if (isTranslator(role)) steps = STEPS_OBJECT.individual.translator;
        }
        const companyData =
            ownCompany && Object.keys(ownCompany).length
                ? {...ownCompany}
                : {
                      type: 'business',
                      businessRole: 'director',
                  };
        this.state = {
            steps: steps,
            currentStep: this.getFirstStep(steps, role, user.accountType, companyData),
            data: {
                ...user,
                birthday: birthday ? new Date(birthday) : null,
                costPerHour: costPerHour,
                phone: user.phone || '',
                company,
                hourlyRate: hourlyRate || {
                    'offline-home': '',
                    vfgd: '',
                    'video-idi': '',
                    'offline-focus-group': '',
                    'offline-interview': '',
                    online: '',
                },
            },
            companyData,
            isMedical,
            medicalData: user.medical || {},
            isBusiness,
            businessData: user.business || {},
            errors: {},
            hasVat: true,
            vatLoading: false,
            otherRole:
                (ownCompany &&
                    ownCompany.businessRole !== 'director' &&
                    ownCompany.businessRole !== 'owner' &&
                    ownCompany.businessRole) ||
                '',
            skip: false,
        };
        this.user = props.userContext.user;
        this.isModal = props.isModal;
    }

    getDefaultSteps(role, t) {
        switch (role) {
            case UserRoles.companyAdmin:
                return COMPANY_ADMIN_FIRST_STEPS;
            case UserRoles.moderator:
                return COMPANY_ADMIN_FIRST_STEPS;
            case UserRoles.translator:
                return COMPANY_ADMIN_FIRST_STEPS;
            default:
                return RESOURCE_STEPS;
        }
    }

    getFirstStep(startSteps, role, accountType, companyData) {
        const {user} = this.props.userContext;

        if (isRespondent(role)) {
            let firstUnfilledStep = 0;
            for (const step of startSteps) {
                const stepKey = Object.keys(STEPS).find(key => STEPS[key] === step);
                let notFilled = false;
                for (const field of STEPS_FIELDS[role][stepKey]) {
                    if (!user[field]) notFilled = true;
                }
                if (notFilled) break;
                firstUnfilledStep++;
            }
            return startSteps[Math.min(startSteps.length - 1, firstUnfilledStep)];
        }

        if (!accountType) return startSteps[0];

        let firstUnfilledStep = 0;
        for (const step of startSteps) {
            const stepKey = Object.keys(STEPS).find(key => STEPS[key] === step);
            let notFilled = false;

            const isCompanyDataSteps = stepKey === 'aboutCompany' || stepKey === 'companyAddress';

            for (const field of STEPS_FIELDS[role][accountType][stepKey]) {
                const isHourlyRateFields = Object.values(interviewTypesValues).includes(field);
                const isTranslationField = field === 'translateLanguages';

                if (isCompanyDataSteps) {
                    if (!companyData[field]) notFilled = true;
                } else if (isHourlyRateFields) {
                    if (!user.hourlyRate || (user.hourlyRate && !user.hourlyRate[field])) notFilled = true;
                } else if (isTranslationField) {
                    if (!user.translateLanguages || !user.translateLanguages.length) notFilled = true;
                } else if (!user[field]) notFilled = true;
            }
            if (notFilled) break;
            firstUnfilledStep++;
        }
        return startSteps[Math.min(startSteps.length - 1, firstUnfilledStep)];
    }

    async validate(step) {
        const {currentStep, data, companyData, medicalData, businessData} = this.state;
        const {
            userContext: {user},
            t,
        } = this.props;
        let actualStep = step || currentStep;
        switch (actualStep) {
            case STEPS.personalBasics:
                return await validatePersonalBasics(user.role, data, t, this.validatePhone);
            case STEPS.address:
                return this.validateAddressFields(data);
            case STEPS.personalAddress:
                return this.validateAddressFields(data);
            case STEPS.workDetails:
                return validateWorkDetails(user.role, data, t);
            case STEPS.paymentDetails:
                return validatePaymentDetails(user.role, data, t);
            case STEPS.translation:
                return validateTranslation(data, t);
            case STEPS.aboutCompany:
                return validateAboutCompany(user.role, companyData, t, this.state.hasVat);
            case STEPS.companyAddress:
                return validateCompanyAddress(companyData, t);
            case STEPS.isMedical:
                return validateMedicalData(medicalData, t);
            case STEPS.isBusiness:
                return validateBusinessData(businessData, t);
            case STEPS.companyRepresentative:
                return validateRepresentative(
                    user.role,
                    {...data, ...companyData},
                    t,
                    this.validatePhone,
                    this.state.otherRole,
                );
            default:
                return {};
        }
    }

    validateAll = async () => {
        for (let step of this.state.steps) {
            const res = await this.validate(step);
            if (Object.keys(res).length > 0) return true;
        }
        return false;
    };

    onChange = (name, value, cb) => {
        if (name === 'accountType') {
            const type = value.toLowerCase();
            const role = this.user.role.toLowerCase();
            const steps = STEPS_OBJECT[type][role] || RESOURCE_STEPS;
            const {companyData} = this.state;
            const company =
                companyData && Object.keys(companyData).length
                    ? {...companyData}
                    : {
                          type: 'business',
                          businessRole: 'director',
                      };
            this.setState({steps, companyData: company}, () => cb && cb());
        }
        const {data} = this.state;
        if (name === 'summary' || name === 'full') {
            const maxRate = 100000;
            value = parseInt(value.replace(/[^0-9.]/g, ''));
            if (value > maxRate) value = maxRate;
        }
        data[name] = value;
        this.setState({data: {...data}});
    };

    onChangeHourlyRate = (name, value) => {
        const {data} = this.state;
        data.hourlyRate[name] = value;
        this.setState({data: {...data}});
    };

    onChangeCompanyValue = (name, value) => {
        const {companyData} = this.state;
        companyData[name] = value;
        this.setState({companyData: {...companyData}});
    };

    onMedicalDataChange = ({target}) => {
        const {name, value} = target;
        const {medicalData} = this.state;
        medicalData[name] = value;
        if (name === 'speciality') medicalData['subSpeciality'] = null;
        this.setState({medicalData: {...medicalData}});
    };

    onBusinessDataChange = ({target}) => {
        const {name, value} = target;
        const {businessData} = this.state;
        businessData[name] = value;
        this.setState({businessData: {...businessData}});
    };

    changeStep = async step => {
        if (this.state.steps.indexOf(step) <= this.state.steps.indexOf(this.state.currentStep)) {
            return this.setState({currentStep: step});
        }

        this.setState({errors: await this.validate()}, () => {
            if (Object.keys(this.state.errors).length) return;
            this.setState({currentStep: step});
        });
    };

    nextStep = async () => {
        const {currentStep, steps} = this.state;
        const currentIndex = steps.indexOf(currentStep);
        if (!steps[currentIndex + 1]) {
            await this.getStartedClick();
        } else {
            await this.changeStep(steps[currentIndex + 1]);
        }
    };

    previousStep = () => {
        const {currentStep, steps} = this.state;
        const currentIndex = steps.indexOf(currentStep);

        this.changeStep(steps[currentIndex - 1]);
    };

    unsetFields = data => {
        const unsetData = {};
        const {employmentStatus} = data;
        if (NO_COMPANY_STATUSES.includes(employmentStatus)) {
            unsetData.occupation = '';
            unsetData.company = '';
            delete data.occupation;
            delete data.company;
        }

        return unsetData;
    };

    getStartedClick = async () => {
        const {t} = this.props;
        const anotherTabsErrorExist = await this.validateAll();
        const errors = await this.validate();
        if (anotherTabsErrorExist) errors.common = t('welcome:errors.common');
        this.setState({errors});
        if (Object.keys(errors).length) return;
        const {data} = this.state;
        const {
            userContext: {saveProfile, finishRegistration, setShowWelcomeModal},
        } = this.props;
        const welcomeData = {...data, welcomeFilled: true};
        const isBusiness = welcomeData.business && Object.values(welcomeData.business).length > 0;
        const isMedical = welcomeData.medical && Object.values(welcomeData.medical).length > 0;
        if (isBusiness) {
            delete welcomeData.business;
            welcomeData.businessStatus = 'Requested';
        }
        if (isMedical) {
            delete welcomeData.medical;
            welcomeData.medicalStatus = 'Requested';
        }

        const unsetFields = this.unsetFields(welcomeData);

        try {
            await saveProfile({...welcomeData, unsetFields});
            await finishRegistration();
            setShowWelcomeModal(false);
        } catch (error) {
            return;
        }
        await this.props.chatContext.joinChat();
    };

    saveCompanyInfoClick = async () => {
        this.setState({errors: await this.validate()}, () => {
            const {
                userContext: {saveCompanyInfo},
            } = this.props;
            const {companyData, errors} = this.state;
            const correctCompanyData = {...companyData};
            if (correctCompanyData.businessRole === 'other') {
                correctCompanyData.businessRole = this.state.otherRole;
            }
            if (Object.keys(errors).length) return;
            saveCompanyInfo({...correctCompanyData}, err => {
                if (err) return;
                this.nextStep();
            });
        });
    };

    saveMedicalInfo = async () => {
        this.setState({errors: await this.validate()}, () => {
            const {
                userContext: {setRespondentMedicalInfo},
            } = this.props;

            const {medicalData, errors} = this.state;

            if (Object.keys(errors).length) return;

            setRespondentMedicalInfo({medical: medicalData}).then(this.nextStep);
        });
    };

    saveBusinessInfo = async () => {
        this.setState({errors: await this.validate()}, async () => {
            const {
                userContext: {setRespondentBusinessInfo},
            } = this.props;

            const {businessData, errors} = this.state;
            if (Object.keys(errors).length) return;

            await setRespondentBusinessInfo({business: businessData});

            this.nextStep();
        });
    };

    profileTypeToggle = ({target}) => {
        const {deleteRespondentMedicalInfo, deleteRespondentBusinessInfo} = this.props.userContext;
        const profileTypeState = {[target.name]: !this.state[target.name]};

        const newSteps = profileTypeState[target.name]
            ? this.state.steps.concat(STEPS[target.name])
            : this.state.steps.filter(step => step !== STEPS[target.name]);

        let newMedical = this.state.medicalData;
        if (!profileTypeState.isMedical && target.name === 'isMedical') {
            newMedical = {};
            deleteRespondentMedicalInfo({});
        }

        let newBusiness = this.state.businessData;
        if (!profileTypeState.isBusiness && target.name === 'isBusiness') {
            newBusiness = {};
            deleteRespondentBusinessInfo({});
        }

        this.setState({
            ...profileTypeState,
            steps: newSteps,
            medicalData: newMedical,
            businessData: newBusiness,
        });
    };

    saveUserInfo = async () => {
        this.setState({errors: await this.validate()}, async () => {
            const {
                userContext: {saveProfile},
            } = this.props;
            const {data, errors} = this.state;
            if (Object.keys(errors).length) return;

            data.phone = stripPhoneNumber(data.phone);
            await saveProfile(data, err => {
                if (err) return;
                this.nextStep();
            });
        });
    };

    saveUserAndCompanyInfo = async () => {
        this.setState({errors: await this.validate()}, async () => {
            const {
                userContext: {saveProfile, saveCompanyInfo},
            } = this.props;
            const {data, companyData, errors} = this.state;
            const correctCompanyData = {...companyData};
            if (correctCompanyData.businessRole === 'other') {
                correctCompanyData.businessRole = this.state.otherRole;
            }
            if (Object.keys(errors).length) return;

            data.phone = stripPhoneNumber(data.phone);

            saveCompanyInfo({...correctCompanyData});
            await saveProfile(data, err => {
                if (err) return;
                this.nextStep();
            });
        });
    };

    validateAddressFields = ({house, street, city, state, postalCode, country}) => {
        const errors = {};
        const {t} = this.props;
        if (!city || !city.trim()) {
            errors.city = 'welcome:errors.city';
        }
        if (!state || !state.trim()) {
            errors.state = 'welcome:errors.state';
        }
        if (!postalCode || !postalCode.trim()) {
            errors.postalCode = 'welcome:errors.postalCode';
        }
        if (!country) {
            errors.country = 'welcome:errors.country';
        }
        if (!street || !street.trim()) {
            errors.street = 'welcome:errors.street';
        }
        if (!house || !house.trim()) {
            errors.house = 'welcome:errors.house';
        }

        validateLength({house, city, state, postalCode, country, street}, errors, t);

        this.setState({errors});
        return errors;
    };

    validateCompanyAddress = ({house, street, city, state, postalCode, country}) => {
        const {t} = this.props;
        const errors = validateCompanyAddress({house, street, city, state, postalCode, country}, t);
        this.setState({errors});
        return errors;
    };

    validatePhone = async () => {
        const {data} = this.state;
        const {t} = this.props;
        if (!data.phone) return;

        const {
            userContext: {validatePhoneNumber},
        } = this.props;

        try {
            const result = await validatePhoneNumber(data.phone);
            const phoneError = result.valid ? '' : t('welcome:errors.invalidPhone');

            this.setState({errors: {...this.state.errors, phone: phoneError}});
            return result.valid;
        } catch (e) {
            this.setState({errors: {...this.state.errors, phone: t('welcome:errors.invalidPhone')}});
            return false;
        }
    };

    validateVAT = async () => {
        const {t} = this.props;
        const {vat} = this.state.companyData;
        if (!vat || vat.length < 9 || vat.length > 16) {
            this.setState({errors: {...this.state.errors, vat: t('welcome:errors.invalidVat')}});
            return false;
        }
        try {
            this.setState({vatLoading: true});
            const data = await validateVatNumber(vat);
            if (!data || (data && data.code === 400) || !data.valid) {
                this.setState({errors: {...this.state.errors, vat: t('welcome:errors.invalidVat')}, vatLoading: false});
                return false;
            }
            const {company_name, company_address} = data.company;
            const address = await getLocationAutocomplete(company_address.replaceAll('\n', ' ').slice(0, 12));
            this.setState({
                companyData: {
                    ...this.state.companyData,
                    name: company_name,
                    address: address[0],
                },
                errors: {
                    ...this.state.errors,
                    vat: null,
                },
                vatLoading: false,
            });
            return true;
        } catch (e) {
            this.setState({errors: {...this.state.errors, vat: t('welcome:errors.invalidVat')}, vatLoading: false});
            return false;
        }
    };

    switchVat = () => {
        const errors = this.state.errors;
        delete errors.vat;
        this.setState(prevState => ({
            hasVat: !prevState.hasVat,
            errors: {
                ...errors,
            },
        }));
    };

    changeOtherRole = str => {
        this.setState({
            otherRole: str,
        });
    };
    validateField = (data, field) => {
        const errors = {...this.state.errors};
        const validationError = validateSingleField(data, field);
        if (!validationError) {
            delete errors[field];
        }
        return this.setState({errors: errors});
    };

    validateTranslationFields = (languageRows, cb) => {
        const {t} = this.props;
        let errors = validateTranslation({translateLanguages: languageRows}, t);

        if (Object.keys(errors).length === 0) {
            errors = {...this.state.errors};
            delete errors.translate;
            return this.setState({errors: {...errors}}, cb && cb(errors));
        }
        this.setState({errors: {...this.state.errors, ...errors}}, cb && cb(errors));
    };

    handleSkipRegistration = async () => {
        const {
            userContext: {setShowWelcomeModal, user, saveProfile, saveCompanyInfo},
        } = this.props;
        const {data, currentStep, companyData} = this.state;
        const stepKey = Object.keys(STEPS).find(key => STEPS[key] === currentStep);
        const dataWithStepFields = {};
        const companyDataWithStepFields = {};
        const stepFields = isRespondent(user.role)
            ? STEPS_FIELDS[user.role][stepKey]
            : STEPS_FIELDS[user.role][user.accountType][stepKey];

        const isCompanyAddressStep = stepKey === 'companyAddress';

        for (const field of stepFields) {
            const isHourlyRateFields = Object.values(interviewTypesValues).includes(field);
            const isTranslationField = field === 'translationLanguages';
            const isPhoneField = field === 'phone';

            if (isCompanyAddressStep) {
                if (companyData[field]) companyDataWithStepFields[field] = companyData[field];
            } else if (isHourlyRateFields) {
                dataWithStepFields.hourlyRate = data.hourlyRate;
            } else if (isTranslationField) {
                if (data[field] && data[field].length) dataWithStepFields[field] = data[field];
            } else if (isPhoneField) {
                const result = await this.validatePhone();
                if (result) dataWithStepFields[field] = data[field];
            } else if (data[field]) dataWithStepFields[field] = data[field];
        }
        await saveProfile(dataWithStepFields);
        await saveCompanyInfo(companyDataWithStepFields);
        if (this.isModal) setShowWelcomeModal(false);
        else this.setState({skip: true});
        await this.props.chatContext.joinChat();
    };

    renderForm = () => {
        const {
            currentStep,
            data,
            companyData,
            steps,
            errors,
            isMedical,
            isBusiness,
            vatLoading,
            hasVat,
            otherRole,
        } = this.state;
        const {role} = this.props.userContext.user;
        const isLastStep = steps.indexOf(currentStep) === steps.length - 1;

        switch (currentStep) {
            case STEPS.accountType:
                return (
                    <AccountTypeForm
                        onNextStep={this.saveUserInfo}
                        onPreviousStep={this.previousStep}
                        onChange={this.onChange}
                        isLastStep={isLastStep}
                        isModal={this.isModal}
                    />
                );
            case STEPS.personalBasics:
                return (
                    <PersonalBasicsForm
                        onNextStep={this.saveUserInfo}
                        onChange={this.onChange}
                        data={data}
                        role={role}
                        errors={errors}
                        isLastStep={isLastStep}
                        validatePhone={this.validatePhone}
                        validate={this.validateField}
                        isModal={this.isModal}
                    />
                );
            case STEPS.address:
                return (
                    <AddressForm
                        onNextStep={this.saveUserInfo}
                        onPreviousStep={this.previousStep}
                        onChange={this.onChange}
                        data={data}
                        isLastStep={isLastStep}
                        errors={errors}
                        role={role}
                        validate={this.validateField}
                        validateForm={this.validateAddressFields}
                        isModal={this.isModal}
                    />
                );
            case STEPS.personalAddress:
                return (
                    <AddressForm
                        onNextStep={this.saveUserInfo}
                        onPreviousStep={this.previousStep}
                        onChange={this.onChange}
                        data={data}
                        isLastStep={isLastStep}
                        errors={errors}
                        role={role}
                        validate={this.validateField}
                        validateForm={this.validateAddressFields}
                        isModal={this.isModal}
                    />
                );
            case STEPS.paymentDetails:
                return (
                    <PaymentDetailsForm
                        data={data}
                        onChange={this.onChange}
                        onNextStep={this.saveUserInfo}
                        onPreviousStep={this.previousStep}
                        isLastStep={isLastStep}
                        errors={errors}
                        validate={this.validateField}
                        onChangeHourlyRate={this.onChangeHourlyRate}
                        role={role}
                        isModal={this.isModal}
                    />
                );
            case STEPS.aboutCompany:
                return (
                    <AboutCompanyForm
                        onNextStep={this.saveCompanyInfoClick}
                        onPreviousStep={this.previousStep}
                        onChange={this.onChangeCompanyValue}
                        data={companyData}
                        errors={errors}
                        isLastStep={isLastStep}
                        validateVat={this.validateVAT}
                        hasVat={hasVat}
                        switchVat={this.switchVat}
                        vatLoading={vatLoading}
                        validate={this.validateField}
                        role={role}
                        isModal={this.isModal}
                    />
                );
            case STEPS.companyAddress:
                return (
                    <CompanyAddressForm
                        onNextStep={this.saveCompanyInfoClick}
                        onPreviousStep={this.previousStep}
                        onChange={this.onChangeCompanyValue}
                        data={companyData}
                        errors={errors}
                        isLastStep={isLastStep}
                        validate={this.validateField}
                        validateForm={this.validateCompanyAddress}
                        role={role}
                        isModal={this.isModal}
                    />
                );
            case STEPS.companyRepresentative:
                return (
                    <CompanyRepresentativeForm
                        onNextStep={this.saveUserAndCompanyInfo}
                        onChange={this.onChange}
                        onChangeCompany={this.onChangeCompanyValue}
                        companyData={companyData}
                        data={data}
                        role={role}
                        errors={errors}
                        isLastStep={isLastStep}
                        validatePhone={this.validatePhone}
                        otherRole={otherRole}
                        changeOtherRole={this.changeOtherRole}
                        validate={this.validateField}
                        isModal={this.isModal}
                    />
                );
            case STEPS.workDetails:
                return (
                    <WorkDetailsForm
                        data={data}
                        onChange={this.onChange}
                        onNextStep={this.saveUserInfo}
                        onPreviousStep={this.previousStep}
                        isLastStep={isLastStep}
                        errors={errors}
                        profileTypeToggle={this.profileTypeToggle}
                        isMedical={isMedical}
                        isBusiness={isBusiness}
                        validate={this.validateField}
                        role={role}
                        isModal={this.isModal}
                    />
                );
            case STEPS.translation:
                return (
                    <TranslationForm
                        data={data}
                        onChange={this.onChange}
                        onNextStep={this.nextStep}
                        onPreviousStep={this.previousStep}
                        errors={errors}
                        isLastStep={isLastStep}
                        validateForm={this.validateTranslationFields}
                    />
                );
            case STEPS.isMedical:
                return (
                    <MedicalForm
                        data={this.state.medicalData}
                        errors={errors}
                        onNextStep={this.saveMedicalInfo}
                        onPreviousStep={this.previousStep}
                        onChange={this.onMedicalDataChange}
                        isLastStep={isLastStep}
                    />
                );
            case STEPS.isBusiness:
                return (
                    <BusinessForm
                        data={this.state.businessData}
                        errors={errors}
                        onPreviousStep={this.previousStep}
                        onNextStep={this.saveBusinessInfo}
                        onChange={this.onBusinessDataChange}
                        isLastStep={isLastStep}
                    />
                );
            default:
                return null;
        }
    };

    render() {
        const {steps, currentStep, skip} = this.state;
        const {
            t,
            userContext: {user},
        } = this.props;
        const isStepWithSliders = currentStep !== STEPS.accountType || steps.length > 1;
        const isSkipAvailable =
            currentStep !== STEPS.personalBasics &&
            currentStep !== STEPS.accountType &&
            currentStep !== STEPS.aboutCompany;
        const defaultStepperColor = this.isModal ? '#f3f3f4' : '#FFF';
        return (
            <div
                className={cn(style.welcomePageContainer, {
                    [style.modal]: this.isModal,
                    [style.wide]: isTranslator(user.role) || isModerator(user.role),
                })}
            >
                {this.isModal && <div className={style.modalHeader}>{t('welcome:text.profile')}</div>}
                <div className={cn(style.header, {[style.modal]: this.isModal})}>
                    {t(getTextByStep(currentStep, user.role, true))}
                </div>
                <SimpleMessage className={cn(style.subheader, {[style.modal]: this.isModal})}>
                    {t(getTextByStep(currentStep, user.role, false, user.accountType))}
                </SimpleMessage>
                <div className={cn(style.welcomeFormContainer, {[style.modal]: this.isModal})}>
                    {isStepWithSliders && (
                        <div className={cn(style.stepper, {[style.modal]: this.isModal})}>
                            <OptionSlider
                                options={steps}
                                onChange={this.changeStep}
                                value={this.state.currentStep}
                                vertical
                                defaultColor={defaultStepperColor}
                                textClassName={this.isModal && style.withWhiteText}
                            />
                        </div>
                    )}
                    {this.renderForm()}
                </div>
                {isSkipAvailable && (
                    <div
                        className={cn(style.bottomSkip, {
                            [style.modal]: this.isModal,
                            [style.wideLeft]: isTranslator(user.role) || isModerator(user.role),
                        })}
                        onClick={this.handleSkipRegistration}
                    >
                        {t('welcome:text.skip')}
                        <BackIcon className={style.skip} />
                    </div>
                )}
                {skip && !this.isModal && <Redirect to={getRedirectPath(user)} />}
            </div>
        );
    }
}

export default withTranslation()(UserContextConsumer(ChatContextConsumer(Welcome)));
